/**
 * Onboarding Banner Component
 * @implements US-021 - Automatic Conversation Initiation
 * @implements TS-088 - Onboarding flow restrictions (first project)
 * @implements FS-002.7.1 - Automatic Conversation Initiation (UI indicator)
 *
 * Displays a banner during onboarding to guide first-time users.
 * Shows progress through the one-exchange requirement.
 */

import { Box, Alert, AlertTitle, LinearProgress, Stack } from '@mui/material';
import {
  School as OnboardingIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from '@mui/icons-material';

/**
 * Props for OnboardingBanner component
 */
export interface OnboardingBannerProps {
  /** Whether the user has sent their first message */
  hasSentMessage: boolean;
  /** Whether the AI PM has responded */
  hasReceivedResponse: boolean;
  /** Whether the exchange is complete (both conditions met) */
  hasCompletedExchange: boolean;
}

/**
 * OnboardingBanner Component
 * @implements TS-088 - Onboarding flow restrictions
 *
 * Shows onboarding status and guides the user through completing
 * their first exchange with the AI PM.
 *
 * Progress states:
 * 1. Initial: "Send a message to the AI PM"
 * 2. Message sent: "Waiting for AI PM response..."
 * 3. Complete: "Great job! You can now navigate freely."
 *
 * @example
 * ```tsx
 * <OnboardingBanner
 *   hasSentMessage={true}
 *   hasReceivedResponse={false}
 *   hasCompletedExchange={false}
 * />
 * ```
 */
export const OnboardingBanner = ({
  hasSentMessage,
  hasReceivedResponse,
  hasCompletedExchange,
}: OnboardingBannerProps) => {
  // Calculate progress (0-100)
  const progress = hasCompletedExchange ? 100 : hasSentMessage ? 50 : 0;

  // Determine severity based on completion state
  const severity = hasCompletedExchange ? 'success' : 'info';

  // Determine message based on state
  const getMessage = () => {
    if (hasCompletedExchange) {
      return "Great job! You've completed your first exchange. You can now explore the platform freely.";
    }
    if (hasSentMessage && !hasReceivedResponse) {
      return 'Waiting for the AI Project Manager to respond...';
    }
    return 'Send a message to the AI Project Manager to complete your onboarding. You can ask about your project or answer the questions above.';
  };

  return (
    <Alert
      severity={severity}
      icon={<OnboardingIcon />}
      sx={{
        mb: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      <AlertTitle>
        {hasCompletedExchange
          ? 'Onboarding Complete!'
          : 'Welcome to Your First Project'}
      </AlertTitle>
      <Box sx={{ mb: 2 }}>{getMessage()}</Box>

      {/* Progress indicator */}
      {!hasCompletedExchange && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 1, height: 6, borderRadius: 3 }}
          />
          <Stack direction="row" spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasSentMessage ? (
                <CheckIcon color="success" fontSize="small" />
              ) : (
                <UncheckedIcon color="disabled" fontSize="small" />
              )}
              <Box
                component="span"
                sx={{
                  fontSize: '0.875rem',
                  color: hasSentMessage ? 'success.main' : 'text.secondary',
                }}
              >
                Send a message
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasReceivedResponse ? (
                <CheckIcon color="success" fontSize="small" />
              ) : (
                <UncheckedIcon color="disabled" fontSize="small" />
              )}
              <Box
                component="span"
                sx={{
                  fontSize: '0.875rem',
                  color: hasReceivedResponse
                    ? 'success.main'
                    : 'text.secondary',
                }}
              >
                Receive AI PM response
              </Box>
            </Box>
          </Stack>
        </Box>
      )}
    </Alert>
  );
};
